import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { XCircle } from "lucide-react";
import { controlLoading } from "@/services/slices/app.slice";
import {
  BusinessRegisterAPI,
  CreateBusinessSignupCheckoutAPI,
  getBusinessSignupPromoAPI,
} from "@/services/api/user/businessSignup";
import type { BusinessPlanType } from "@/services/api/business/getBusinessPlan";
import Modal from "@/components/Modal";
import Icon from "@/components/Icon";
import { AUTH_CARD_CLASS } from "@/components/auth/authStyles";
import BusinessTermsContent from "@/pages/public/BusinessTermsContent";
import { useLegalDocument } from "@/hooks/useLegalDocument";
import {
  buildBusinessSignupQuery,
  getBusinessSignupBrImage,
  getBusinessSignupProfile,
  readBusinessSignupParams,
  saveBusinessSignupPending,
  getBusinessSignupPending,
  clearBusinessSignupPending,
  isBusinessSignupRegistered,
  markBusinessSignupRegistered,
  clearBusinessSignupRegistered,
} from "./businessSignupState";

export const BUSINESS_TERMS_VERSION = "2026-11-01";

const YEARLY_PRICE = 500;

const PLAN_TILES: { type: BusinessPlanType; price: number }[] = [
  { type: "yearly", price: YEARLY_PRICE },
];

const BusinessPlanPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = readBusinessSignupParams(search);
  const query = new URLSearchParams(search);
  const checkoutSessionId = query.get("checkout_session_id") || "";

  // On return from Stripe the signup params are gone from the URL, so restore
  // the plan the user picked (and validated) from the pending blob.
  const [planType, setPlanType] = useState<BusinessPlanType>("yearly");
  const [termsAccepted, setTermsAccepted] = useState(
    () => Boolean(getBusinessSignupPending()?.terms_accepted),
  );
  const [termsError, setTermsError] = useState("");
  const [termsModalVisible, setTermsModalVisible] = useState(false);
  const [modalAlertVisible, setModalAlertVisible] = useState(false);
  const [modalAlertText, setModalAlertText] = useState("");
  const [processing, setProcessing] = useState(false);
  const [isPromoFirst100, setIsPromoFirst100] = useState(false);
  const autoRegisterRef = useRef(false);
  const { doc: businessTermsDoc } = useLegalDocument("business_terms");
  const termsVersion = businessTermsDoc?.version || BUSINESS_TERMS_VERSION;

  useEffect(() => {
    let cancelled = false;
    getBusinessSignupPromoAPI().then((resp) => {
      if (cancelled) return;
      if (resp?.status === 0 && resp?.result) {
        setIsPromoFirst100(Boolean(resp.result.is_promo_first_100));
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const onPressContinue = async () => {
    clearBusinessSignupRegistered();
    if (!termsAccepted) {
      setTermsError("Please accept the Business Terms & Conditions");
      return;
    }
    if (processing) return;
    setTermsError("");
    setProcessing(true);
    dispatch(controlLoading({ showLoading: true }));
    const brImage = getBusinessSignupBrImage();
    const profileIcon = getBusinessSignupProfile();
    const pending = {
      username: params.username || "",
      companyEmail: params.companyEmail || "",
      phone: params.phone || "",
      regionCode: params.regionCode || "",
      password: params.password || "",
      companyName: params.companyName || "",
      companyAddress: params.companyAddress || "",
      brNumber: params.brNumber || "",
      plan_type: planType,
      terms_version: termsVersion,
      terms_accepted: true,
      br_image: brImage,
      profile_icon: profileIcon,
    };
    saveBusinessSignupPending(pending);
    const resp = await CreateBusinessSignupCheckoutAPI({
      username: params.username || "",
      company_email: params.companyEmail || "",
      phone_num: params.phone || "",
      password: params.password || "",
      company_name: params.companyName || "",
      br_number: params.brNumber || "",
      br_image: brImage,
      company_address: params.companyAddress || "",
      profile_icon: profileIcon,
      plan_type: planType,
      return_path: "/business/signup/plan",
    });
    dispatch(controlLoading({ showLoading: false }));
    setProcessing(false);
    if (resp?.status === -20000) {
      setModalAlertText(t("signup.businessEmailExisted"));
      return setModalAlertVisible(true);
    }
    if (resp?.status === -20001) {
      setModalAlertText(t("signup.usernameExisted"));
      return setModalAlertVisible(true);
    }
    if (resp?.status === -20002) {
      setModalAlertText(t("Can`t register account"));
      return setModalAlertVisible(true);
    }
    if (resp?.status === 0 && resp?.result?.url) {
      // createCheckout uploads the images; keep those URLs so registration does
      // not re-upload (and the session id is bound to the validated card).
      saveBusinessSignupPending({
        ...pending,
        br_image: resp.result.br_image || pending.br_image,
        profile_icon: resp.result.icon || pending.profile_icon,
      });
      window.location.href = resp.result.url;
      return;
    }
    if (!resp || resp.success === false || resp.status === 2) {
      setModalAlertText(
        typeof resp?.result === "string" && resp.result
          ? resp.result
          : (resp?.result?.message || t("Network error, please try again")),
      );
      return setModalAlertVisible(true);
    }
    setModalAlertText(t("Can`t register account"));
    setModalAlertVisible(true);
  };

  const onPressRegister = async () => {
    if (!termsAccepted) {
      setTermsError("Please accept the Business Terms & Conditions");
      return;
    }
    if (processing) return;
    setTermsError("");
    setProcessing(true);
    dispatch(controlLoading({ showLoading: true }));
    const pending = getBusinessSignupPending();
    const brImage = pending?.br_image || getBusinessSignupBrImage();
    const profileIcon = pending?.profile_icon || getBusinessSignupProfile();
    const resp = await BusinessRegisterAPI({
      username: params.username || pending?.username || "",
      company_email: params.companyEmail || pending?.companyEmail || "",
      phone_num: params.phone || pending?.phone || "",
      password: params.password || pending?.password || "",
      company_name: params.companyName || pending?.companyName || "",
      br_number: params.brNumber || pending?.brNumber || "",
      br_image: brImage,
      company_address: params.companyAddress || pending?.companyAddress || "",
      profile_icon: profileIcon,
      plan_type: planType,
      terms_accepted: true,
      terms_version: termsVersion,
      checkout_session_id: checkoutSessionId,
    });
    dispatch(controlLoading({ showLoading: false }));
    setProcessing(false);
    if (resp?.status === -20000) {
      setModalAlertText(t("signup.businessEmailExisted"));
      return setModalAlertVisible(true);
    }
    if (resp?.status === -20001) {
      setModalAlertText(t("signup.usernameExisted"));
      return setModalAlertVisible(true);
    }
    if (resp?.status === -20002) {
      setModalAlertText(t("Can`t register account"));
      return setModalAlertVisible(true);
    }
    if (resp?.status === 0) {
      markBusinessSignupRegistered();
      clearBusinessSignupPending();
      navigate("/business/signup/success");
      return;
    }
    if (!resp || resp.success === false || resp.status === 2) {
      setModalAlertText(
        typeof resp?.result === "string" && resp.result
          ? resp.result
          : (resp?.result?.message || t("Network error, please try again")),
      );
      return setModalAlertVisible(true);
    }
    setModalAlertText(t("Can`t register account"));
    setModalAlertVisible(true);
  };

  useEffect(() => {
    if (!checkoutSessionId || autoRegisterRef.current || isBusinessSignupRegistered()) {
      return;
    }
    autoRegisterRef.current = true;
    onPressRegister();
  }, [checkoutSessionId]);

  return (
    <div className={AUTH_CARD_CLASS}>
      <h1 className="text-2xl font-extrabold text-[#333]">{t("Choose your plan")}</h1>

      <div className="mt-6 grid grid-cols-1 gap-3">
        {PLAN_TILES.map((tile) => {
          const selected = planType === tile.type;
          const unit = t("year");
          return (
            <button
              key={tile.type}
              type="button"
              onClick={() => setPlanType(tile.type)}
              className={`rounded-xl border p-4 text-left transition ${
                selected
                  ? "border-[#ffa217] ring-2 ring-[#ffa217]/30"
                  : "border-gray-200 hover:border-[#ffa217]/60"
              }`}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="text-sm font-bold text-gray-900">
                  {t("Yearly")}
                </span>
              </span>
              <span className="mt-1 block text-sm font-semibold text-gray-900">
                HKD {tile.price}/{unit}
              </span>
            </button>
          );
        })}
      </div>

      <p className="mt-4 text-sm text-gray-500">
        {t("First 100 businesses get their first year free. Eligibility is confirmed after sign-up.")}
      </p>

      <p className="mt-1 text-sm text-gray-500">
        {t("A 12-month minimum term applies to the Yearly plan.")}
      </p>

      <label className="mt-6 flex items-start gap-3">
        <input
          type="checkbox"
          checked={termsAccepted}
          onChange={(e) => {
            setTermsAccepted(e.target.checked);
            if (e.target.checked) setTermsError("");
          }}
          className="mt-0.5 h-4 w-4 shrink-0 accent-[#ffa217]"
        />
        <span className="text-sm text-gray-600">
          {t("I have read and accept the")}{" "}
          <button
            type="button"
            onClick={() => setTermsModalVisible(true)}
            className="font-semibold text-[#ffa217] underline"
          >
            {t("Business Terms & Conditions")}
          </button>
        </span>
      </label>
      {termsError ? (
        <p className="mt-1.5 flex items-center gap-1 text-[13px] text-red-500">
          <XCircle size={14} className="shrink-0" />
          {t(termsError)}
        </p>
      ) : null}

      <div className="mt-8 space-y-3">
        {checkoutSessionId ? (
          <div className="mb-2 text-sm font-medium text-green-600">
            {t("Card validated")}
          </div>
        ) : null}
        <button
          type="button"
          onClick={checkoutSessionId ? onPressRegister : onPressContinue}
          disabled={processing}
          className="w-full rounded-lg bg-[#ffa217] py-3 font-bold text-white transition hover:bg-[#e08f0e] disabled:opacity-60"
        >
          {processing
            ? t("Finalising registration…")
            : checkoutSessionId
              ? t("REGISTER")
              : isPromoFirst100
                ? t("Get started free")
                : t("Subscribe now")}
        </button>

        <button
          type="button"
          onClick={() =>
            navigate(
              `/business/signup/business-info${buildBusinessSignupQuery({ ...params })}`,
            )
          }
          className="w-full rounded-lg bg-red-500 py-3 font-bold text-white transition hover:bg-red-600"
        >
          {t("BACK")}
        </button>
      </div>

      <Modal
        visible={termsModalVisible}
        onClose={() => setTermsModalVisible(false)}
        position="center"
      >
        <div className="flex max-h-[85vh] flex-col overflow-hidden rounded-2xl">
          <div className="flex shrink-0 items-center justify-between bg-[#ffa217] px-5 py-4">
            <div className="text-base font-bold text-white">
              {t("Business Terms & Conditions")}
            </div>
            <button
              type="button"
              onClick={() => setTermsModalVisible(false)}
              aria-label={t("Close")}
            >
              <Icon iconType="Ionicons" name="close" size={24} color="#fff" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto bg-white px-5 py-4">
            <BusinessTermsContent />
            <button
              type="button"
              onClick={() => setTermsModalVisible(false)}
              className="mt-6 w-full rounded-lg bg-[#ffa217] py-3 font-bold text-white transition hover:bg-[#e08f0e]"
            >
              {t("Close")}
            </button>
          </div>
        </div>
      </Modal>

      <Modal visible={modalAlertVisible} onClose={() => setModalAlertVisible(false)}>
        <div className="rounded-2xl bg-white p-6 text-center shadow-lg">
          <div className="mb-4 text-lg font-semibold text-[#333]">{modalAlertText}</div>
          <button
            type="button"
            onClick={() => setModalAlertVisible(false)}
            className="rounded-lg bg-[#ffa217] px-6 py-2.5 font-semibold text-white transition hover:opacity-90"
          >
            {t("OK")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default BusinessPlanPage;
